import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserByEmail } from '../../../hooks/useUsers';
import { usePracticeNames } from '../../../hooks/usePracticeNames';
import { useToast } from '../../../providers/ToastProvider';
import type { UserListItem } from '../../../services/users';

const USER_PARAM = 'user';
const ACTION_PARAM = 'action';
const NEW_USER_PARAM = 'new';
const DELETE_ACTION = 'delete';

function readParam(search: string, key: string): string | null {
    return new URLSearchParams(search).get(key);
}

// Applies a patch of param additions/removals on top of the current search
// string rather than rebuilding it from scratch, so callers that carry other
// query params of their own (e.g. the practice detail page's `?tab=`) keep
// them untouched when the drawer opens or closes.
function withPatchedParams(search: string, patch: Record<string, string | null>): string {
    const params = new URLSearchParams(search);
    for (const [key, value] of Object.entries(patch)) {
        if (value === null) {
            params.delete(key);
        } else {
            params.set(key, value);
        }
    }
    const next = params.toString();
    return next ? `?${next}` : '';
}

export interface UseUserDrawerControllerOptions {
    /**
     * Rows already loaded by the calling page, checked first before falling
     * back to the deep-link fetch-by-email path (mirrors the Users page's
     * original behavior). Omit (or pass an empty array) to always take the
     * deep-link path - the right choice for callers whose row shape isn't a
     * UserListItem (e.g. PracticeUsersTab's PracticeUserRow), since fetching
     * by email is one code path that's always correct regardless of row data.
     */
    loadedUsers?: UserListItem[];
    /** Whether `?user=new` opens the create drawer. Default true. */
    allowCreate?: boolean;
}

export interface UseUserDrawerControllerResult {
    isCreateOpen: boolean;
    editEmail: string | null;
    editUser: UserListItem | null;
    initialConfirmDelete: boolean;
    isDeepLinkLoading: boolean;
    openCreateDrawer: () => void;
    openEditDrawer: (email: string) => void;
    openDeleteConfirm: (email: string) => void;
    closeDrawer: () => void;
}

/**
 * Shared `?user=<email>` / `?user=new` (+ `?action=delete`) drawer
 * orchestration, extracted from the Accounts > Users page so the practice
 * detail page's Users tab can open the same UserDrawer in place instead of
 * navigating away to the Users page. See UsersList.tsx's former
 * readUserParamFromSearch/readActionFromSearch comments for the deep-link and
 * snapshot-ref rationale this preserves unchanged.
 */
export function useUserDrawerController({
    loadedUsers = [],
    allowCreate = true
}: UseUserDrawerControllerOptions = {}): UseUserDrawerControllerResult {
    const location = useLocation();
    const navigate = useNavigate();
    const { showToast } = useToast();

    // Drawer open/closed state is derived directly from the URL rather than
    // mirrored into local state, so browser back/forward (which changes
    // `location.search` without going through the handlers below) closes or
    // reopens the drawer immediately.
    const userParam = readParam(location.search, USER_PARAM);
    const isCreateOpen = allowCreate && userParam === NEW_USER_PARAM;
    const editEmail = userParam && !isCreateOpen ? userParam : null;
    const initialConfirmDelete = readParam(location.search, ACTION_PARAM) === DELETE_ACTION;

    const userFromList =
        editEmail != null
            ? (loadedUsers.find((u) => u.email.toLowerCase() === editEmail.toLowerCase()) ?? null)
            : null;

    // Deep-link case: the row isn't in `loadedUsers` (fresh load, a different
    // filter/sort/page than when the link was shared, or a caller that never
    // passes loaded rows at all) - fetch the single user directly, then
    // resolve its practice ids to names, since GET /users/:email returns
    // practices as bare ids (see services/users.ts's UserRecord vs
    // UserListItem).
    const needsDeepLinkFetch = editEmail != null && userFromList == null;
    const deepLinkQuery = useUserByEmail(needsDeepLinkFetch ? editEmail : undefined);
    const { practices: deepLinkPractices } = usePracticeNames(
        needsDeepLinkFetch ? (deepLinkQuery.data?.practices ?? []) : []
    );

    const deepLinkUser: UserListItem | null =
        needsDeepLinkFetch && deepLinkQuery.data
            ? {
                  id: deepLinkQuery.data.id,
                  email: deepLinkQuery.data.email,
                  fullname: deepLinkQuery.data.fullname,
                  isAdmin: deepLinkQuery.data.isAdmin,
                  practices: deepLinkPractices,
                  createdAt: deepLinkQuery.data.createdAt,
                  lastActivityAt: deepLinkQuery.data.lastActivityAt
              }
            : null;

    // Snapshots the resolved user the first time it's available for the
    // current `editEmail`, then keeps that reference stable for as long as
    // the param doesn't change - a later background refetch (staleTime
    // elapsing, window refocus) must not swap in a new object mid-edit,
    // since useUserDrawerForm resets its local state whenever the `user`
    // prop's reference changes.
    const editUserSnapshotRef = useRef<{ email: string; user: UserListItem } | null>(null);
    if (!editEmail) {
        editUserSnapshotRef.current = null;
    } else if (editUserSnapshotRef.current?.email !== editEmail) {
        const candidate = userFromList ?? deepLinkUser;
        editUserSnapshotRef.current = candidate ? { email: editEmail, user: candidate } : null;
    }
    const editUser =
        editEmail && editUserSnapshotRef.current?.email === editEmail
            ? editUserSnapshotRef.current.user
            : null;
    const isDeepLinkLoading = needsDeepLinkFetch && deepLinkQuery.isLoading;

    const closeDrawer = () => {
        const search = withPatchedParams(location.search, {
            [USER_PARAM]: null,
            [ACTION_PARAM]: null
        });
        navigate(`${location.pathname}${search}`, { replace: true });
    };

    // Bad or stale email in a shared link: drop the param and toast instead
    // of rendering an empty drawer.
    useEffect(() => {
        if (needsDeepLinkFetch && deepLinkQuery.isError) {
            showToast("Couldn't find that user - the link may be out of date", 'error');
            closeDrawer();
        }
        // Only the fetch outcome should retrigger this - closeDrawer/showToast
        // are stable enough in practice and re-running on their identity would
        // risk looping while the drawer is closing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [needsDeepLinkFetch, deepLinkQuery.isError]);

    const openCreateDrawer = () => {
        const search = withPatchedParams(location.search, {
            [USER_PARAM]: NEW_USER_PARAM,
            [ACTION_PARAM]: null
        });
        navigate(`${location.pathname}${search}`);
    };

    const openEditDrawer = (email: string) => {
        const search = withPatchedParams(location.search, {
            [USER_PARAM]: email,
            [ACTION_PARAM]: null
        });
        navigate(`${location.pathname}${search}`);
    };

    const openDeleteConfirm = (email: string) => {
        const search = withPatchedParams(location.search, {
            [USER_PARAM]: email,
            [ACTION_PARAM]: DELETE_ACTION
        });
        navigate(`${location.pathname}${search}`);
    };

    return {
        isCreateOpen,
        editEmail,
        editUser,
        initialConfirmDelete,
        isDeepLinkLoading,
        openCreateDrawer,
        openEditDrawer,
        openDeleteConfirm,
        closeDrawer
    };
}
